/**
 * Dynamic live video layout — use the track's native dimensions (mobile portrait,
 * desktop landscape, etc.) instead of a fixed 9:16 frame.
 */

/** @typedef {'unknown' | 'portrait' | 'landscape' | 'square'} VideoOrientation */

// Target 1080p on the longest side (square `ideal` keeps native aspect — a 16:9
// webcam lands on 1920×1080, a portrait OBS virtual cam on 1080×1920). Cameras
// that can't do 1080p degrade gracefully to their best (e.g. 1280×720). The
// publisher's WebRTC encoder auto-drops resolution under CPU/upload pressure
// (degradationPreference: maintain-framerate), so targeting 1080p is safe.
const LIVE_CAPTURE_MAX_SIDE = 1920
const LIVE_CAPTURE_IDEAL_SIDE = 1920
const LIVE_CAPTURE_MAX_FRAMERATE = 30

/**
 * Browser camera constraints that avoid forcing a landscape aspect ratio.
 * This lets OBS virtual camera publish portrait canvases (9:16) while regular
 * webcams can still negotiate their normal landscape format.
 *
 * @param {'user' | 'environment' | 'left' | 'right'} facingMode
 * @returns {MediaTrackConstraints}
 */
export function buildNativeAspectMediaVideoConstraints(facingMode = 'user') {
  return {
    facingMode,
    width: { ideal: LIVE_CAPTURE_IDEAL_SIDE, max: LIVE_CAPTURE_MAX_SIDE },
    height: { ideal: LIVE_CAPTURE_IDEAL_SIDE, max: LIVE_CAPTURE_MAX_SIDE },
    frameRate: { ideal: LIVE_CAPTURE_MAX_FRAMERATE, max: LIVE_CAPTURE_MAX_FRAMERATE },
  }
}

/**
 * LiveKit capture options. LiveKit flattens `resolution` into MediaTrackConstraints,
 * so using ideal square bounds avoids the library's default 16:9 h720 request.
 *
 * @param {'user' | 'environment' | 'left' | 'right'} facingMode
 */
export function buildNativeAspectLiveKitVideoOptions(facingMode = 'user') {
  return {
    facingMode,
    resolution: {
      width: { ideal: LIVE_CAPTURE_IDEAL_SIDE, max: LIVE_CAPTURE_MAX_SIDE },
      height: { ideal: LIVE_CAPTURE_IDEAL_SIDE, max: LIVE_CAPTURE_MAX_SIDE },
      frameRate: { ideal: LIVE_CAPTURE_MAX_FRAMERATE, max: LIVE_CAPTURE_MAX_FRAMERATE },
    },
  }
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {VideoOrientation}
 */
export function getVideoOrientation(width, height) {
  if (!width || !height) return 'unknown'
  const ratio = width / height
  if (ratio < 0.95) return 'portrait'
  if (ratio > 1.05) return 'landscape'
  return 'square'
}

/**
 * CSS class for orientation-aware layout (see live-native-video.css).
 * @param {VideoOrientation} orientation
 */
export function orientationLayoutClass(orientation) {
  return `live-native-video--${orientation || 'unknown'}`
}

/**
 * Read intrinsic size from a HTMLVideoElement after metadata loads.
 * @param {HTMLVideoElement | null | undefined} el
 * @returns {{ width: number, height: number, orientation: VideoOrientation }}
 */
export function readVideoElementLayout(el) {
  const width = el?.videoWidth ?? 0
  const height = el?.videoHeight ?? 0
  return {
    width,
    height,
    orientation: getVideoOrientation(width, height),
  }
}
